import { defaultStyles, useTooltip } from '@visx/tooltip';
import { UseTooltipInPortal } from '@visx/tooltip/lib/hooks/useTooltipInPortal';
import { TooltipProps } from '@visx/tooltip/lib/tooltips/Tooltip';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { RectReadOnly } from 'react-use-measure';
import { theme } from '../shared/theme';
export const defaultTooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  color: 'black',
};

export interface PlotTooltipProps {
  tooltipOpen: boolean;
  TooltipInPortal: React.FC<TooltipProps>;
  tooltipLeft: number;
  tooltipTop: number;
}

interface Props extends PlotTooltipProps {
  backgroundColor?: string;
  children?: ReactNode;
  style?: React.CSSProperties;
}

export interface TooltipStuff {
  containerBounds: RectReadOnly;
  TooltipInPortal: React.FC<PlotTooltipProps>;
}

// Global tooltip manager to ensure only one tooltip is visible at a time.
// Only instances that are currently shown (or pending to be shown) are tracked as active, so that hiding the
// others is cheap even when there are hundreds of thousands of chart cells with their own tooltip instance.
type TooltipInstanceId = symbol;

const hideFunctionByInstanceId = new Map<TooltipInstanceId, () => void>();
const activeInstanceIds = new Set<TooltipInstanceId>();

export function registerTooltip(id: TooltipInstanceId, hideTooltip: () => void): () => void {
  hideFunctionByInstanceId.set(id, hideTooltip);
  return () => {
    hideFunctionByInstanceId.delete(id);
    activeInstanceIds.delete(id);
  };
}

export function setTooltipActive(id: TooltipInstanceId, active: boolean): void {
  if (active) {
    activeInstanceIds.add(id);
  } else {
    activeInstanceIds.delete(id);
  }
}

export function hideAllTooltipsExcept(currentId: TooltipInstanceId): void {
  // Copy first, since hiding removes instances from the active set
  Array.from(activeInstanceIds).forEach(id => {
    if (id !== currentId) {
      hideFunctionByInstanceId.get(id)?.();
    }
  });
}

export function getActiveTooltipCount(): number {
  return activeInstanceIds.size;
}

export function PlotTooltip({
  tooltipOpen,
  TooltipInPortal,
  tooltipLeft,
  tooltipTop,
  backgroundColor,
  children,
  style,
}: Props) {
  if (!tooltipOpen) {
    return null;
  }
  return (
    <TooltipInPortal
      key={Math.random()} // needed for bounds to update correctly
      left={tooltipLeft}
      top={tooltipTop}
      style={{
        ...defaultTooltipStyles,
        zIndex: theme.zIndexes.tooltipZ,
        backgroundColor: backgroundColor || theme.colors.neutral50,
        ...style,
      }}
    >
      {children}
    </TooltipInPortal>
  );
}

export function usePlotTooltip<Data>(tooltipStuff: UseTooltipInPortal) {
  const { containerBounds, TooltipInPortal } = tooltipStuff;
  const {
    tooltipData,
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipOpen,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip<Data | undefined>({
    tooltipOpen: false,
    tooltipData: undefined,
  });

  // Timeout handles for showing and hiding tooltips (refs, so that scheduling does not re-render the component)
  const showTimeoutIdRef = React.useRef<number | null>(null);
  const hideTimeoutIdRef = React.useRef<number | null>(null);

  // Track current tooltip identity and latest position to prevent flickering
  const currentTooltipIdRef = React.useRef<string | null>(null);
  const latestPositionRef = React.useRef<{ x: number; y: number; data: Data | undefined }>({
    x: 0,
    y: 0,
    data: undefined,
  });

  // Create a unique identifier for this tooltip instance
  const instanceIdRef = React.useRef<symbol>(Symbol('tooltip-instance'));
  const hideTooltipRef = React.useRef(hideTooltip);
  hideTooltipRef.current = hideTooltip;

  // Delay in milliseconds for showing and hiding tooltips
  const showDelay = 150; // 150ms delay before showing tooltip
  const hideDelay = 100; // 100ms delay before hiding tooltip

  // Function to generate unique ID for tooltip data
  const getTooltipId = useCallback((data: unknown): string | null => {
    // Try to use common identifiers if available
    if (!data || typeof data !== 'object') return null;
    const obj = data as Record<string, unknown>;
    if (obj.barIdentifier && typeof obj.barIdentifier === 'string') {
      return `bar-${obj.barIdentifier}`;
    }
    if (obj.antibiotic && typeof obj.antibiotic === 'string') {
      return `antibiotic-${obj.antibiotic}`;
    }
    // Bar and heatmap cells: identify by the stacked key and the sample instead of serialising the whole datum,
    // which includes all genes and Cq rows of the sample.
    const bar = obj.bar as { data?: Record<string, unknown>; index?: unknown } | undefined;
    if (bar && typeof bar === 'object' && typeof obj.key === 'string') {
      return `bar-${obj.key}-${String(bar.data?.sampleId)}-${String(bar.index)}-${obj.general ? 'general' : ''}`;
    }
    return JSON.stringify(data); // Fallback, heavier
  }, []);

  const clearShowTimeout = useCallback(() => {
    if (showTimeoutIdRef.current !== null) {
      window.clearTimeout(showTimeoutIdRef.current);
      showTimeoutIdRef.current = null;
    }
  }, []);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutIdRef.current !== null) {
      window.clearTimeout(hideTimeoutIdRef.current);
      hideTimeoutIdRef.current = null;
    }
  }, []);

  const resetTooltipState = useCallback(() => {
    hideTooltipRef.current();
    currentTooltipIdRef.current = null;
    latestPositionRef.current.data = undefined;
    setTooltipActive(instanceIdRef.current, false);
  }, []);

  // Enhanced hideTooltip function with delay
  const hideTooltipWithDelay = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    hideTimeoutIdRef.current = window.setTimeout(() => {
      hideTimeoutIdRef.current = null;
      resetTooltipState();
    }, hideDelay);
  }, [clearShowTimeout, clearHideTimeout, resetTooltipState]);

  // Immediate hide function (no delay) for use by global manager
  const hideTooltipImmediate = useCallback(() => {
    clearShowTimeout();
    clearHideTimeout();
    resetTooltipState();
  }, [clearShowTimeout, clearHideTimeout, resetTooltipState]);

  // Register this tooltip instance with the global manager
  useEffect(() => {
    const unregister = registerTooltip(instanceIdRef.current, hideTooltipImmediate);
    return () => {
      unregister();
      // Also ensure we clean up any timeouts
      clearShowTimeout();
      clearHideTimeout();
    };
  }, [hideTooltipImmediate, clearShowTimeout, clearHideTimeout]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent | React.TouchEvent, newTooltipData: Data | undefined, enter?: boolean) => {
      if (newTooltipData) {
        // Get an identity for this tooltip data
        const tooltipId = getTooltipId(newTooltipData);
        const isSameTooltipData = tooltipId && tooltipId === currentTooltipIdRef.current;

        // Clear any existing hide timeout
        clearHideTimeout();

        const containerX = ('clientX' in event ? event.clientX : 0) - containerBounds.left;
        const containerY = ('clientY' in event ? event.clientY : 0) - containerBounds.top;

        // Always update the latest position and data reference regardless of whether we show it now
        latestPositionRef.current = {
          x: containerX,
          y: containerY,
          data: newTooltipData,
        };

        // If we have the same tooltip data already showing, just update position
        // without hiding/showing again (prevents flicker within the same bar)
        if (tooltipOpen && isSameTooltipData) {
          updateTooltip({
            tooltipData: newTooltipData,
            tooltipLeft: containerX,
            tooltipTop: containerY,
            tooltipOpen: true,
          });
          return;
        }

        if (enter) {
          // Hide all other tooltips when we're about to show a new one
          hideAllTooltipsExcept(instanceIdRef.current);
          setTooltipActive(instanceIdRef.current, true);

          // Clear any existing show timeout
          clearShowTimeout();

          // Set a new timeout for showing the tooltip
          showTimeoutIdRef.current = window.setTimeout(() => {
            showTimeoutIdRef.current = null;
            // Use the LATEST mouse position when the timer fires, not the original one
            const { x, y, data } = latestPositionRef.current;
            if (data) {
              showTooltip({
                tooltipLeft: x,
                tooltipTop: y,
                tooltipData: data,
              });
              currentTooltipIdRef.current = getTooltipId(data);
            }
          }, showDelay);
        } else {
          // Hide all other tooltips immediately when we're updating an existing one
          hideAllTooltipsExcept(instanceIdRef.current);
          setTooltipActive(instanceIdRef.current, true);

          updateTooltip({
            tooltipData: newTooltipData,
            tooltipLeft: containerX,
            tooltipTop: containerY,
            tooltipOpen: true,
          });
          currentTooltipIdRef.current = tooltipId;
        }
      } else {
        hideTooltipWithDelay();
      }
    },
    [
      containerBounds,
      clearHideTimeout,
      clearShowTimeout,
      showTooltip,
      hideTooltipWithDelay,
      updateTooltip,
      tooltipOpen,
      getTooltipId,
    ],
  );

  return {
    handleMouseMove,
    tooltipData,
    hideTooltip: hideTooltipWithDelay,
    tooltipProps: {
      tooltipOpen: tooltipOpen && !!tooltipData,
      TooltipInPortal,
      tooltipLeft,
      tooltipTop,
    } satisfies PlotTooltipProps,
  };
}
